import TextField, { TextFieldProps } from '@mui/material/TextField';
import { AppImage, brandclub_colors, useStacklineTheme } from '@stackline/ui';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { handleSearchChange } from 'src/components/Header/HeaderSearchContainer';
import { AiIcon } from 'src/components/SvgIcons/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';
import { useUserEmail } from 'src/utils/Hooks/reduxSelectors';
import { getAdvisorHost, getAppName, shouldShowAtlasAdvisorNav } from 'src/utils/app';
import './SearchInputBar.css';

export const SearchTextField = ({
  sx,
  endAdornment,
  customWidth,
  ...rest
}: TextFieldProps & {
  endAdornment?: React.ReactNode;
  customWidth?: number;
}) => {
  const theme = useStacklineTheme();
  // Adjust right padding: 4px when endAdornment present (for 4px gap to button), 14px otherwise
  const rightPadding = endAdornment ? 4 : 14;

  return (
    <TextField
      variant="standard"
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <AppImage
            src="/svg/icons/search.svg"
            alt="search-icon"
            style={{
              marginRight: 10,
              height: 13,
              width: 13
            }}
          />
        ),
        endAdornment,
        autoComplete: 'off',
        id: 'type-search'
      }}
      sx={{
        ...{
          borderRadius: 17,
          border: `1px solid ${theme.colors.primaryGray}`,
          height: 32,
          width: customWidth ?? 290,
          backgroundColor: brandclub_colors.white,
          '& div.MuiInput-root': {
            padding: `0 ${rightPadding}px 0 14px`,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            boxSizing: 'border-box',
            '& input': {
              padding: 0,
              fontSize: '14px',
              fontFamily: 'Roboto',
              color: theme.colors.primary,
              flex: 1
            }
          },
          '.MuiInput-input::placeholder': {
            color: theme.colors.secondary,
            fontSize: '14px'
          }
        },
        ...sx
      }}
      {...rest}
    />
  );
};

const NewSearchInput: FC<any> = ({ history }) => {
  const [input, setInput] = useState('');
  const { pathname } = window.location;
  const appName = getAppName();
  const theme = useStacklineTheme();

  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const retailer = useAppSelector((state) => state.retailer);
  const userEmail = useUserEmail();

  // Check if Ask Advisor button should be shown (Atlas app only + feature flag)
  const showAdvisorButton = shouldShowAtlasAdvisorNav();

  // Cache the advisor host since hostname doesn't change during session
  const advisorHost = useMemo(() => getAdvisorHost(), []);

  useEffect(() => {
    if (pathname !== '/search') {
      setInput('');
    }
  }, [pathname]);

  /**
   * Opens the Advisor application in a new tab.
   * If search text is provided, it will be passed as a prompt parameter.
   * The userEmail is included for identity verification on the Advisor side.
   * Only triggered by click - not by keyboard events.
   */
  const handleAskAdvisor = () => {
    const trimmedInput = input.trim();

    // Build URL with userEmail for identity verification
    const params = new URLSearchParams();
    if (trimmedInput) {
      params.set('prompt', trimmedInput);
    }
    if (userEmail) {
      params.set('userEmail', userEmail);
    }

    const queryString = params.toString();
    const advisorUrl = queryString ? `https://${advisorHost}/?${queryString}` : `https://${advisorHost}/`;
    window.open(advisorUrl, '_blank', 'noopener,noreferrer');
  };

  // Ask Advisor button as endAdornment (only for Atlas with feature flag)
  // Figma specs: 112px x 24px
  // Icon: 20x20px (no container padding)
  // Padding: 2px top/bottom, 4px left, 8px right
  // Gap between icon and text: 4px
  // Rainbow rotating border with hover background #F6F7F8
  const askAdvisorButton = showAdvisorButton ? (
    <span
      style={{
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Rainbow border wrapper - the gradient rotates around the button */}
      <span className="ask-advisor-btn-wrapper">
        <button
          type="button"
          className="ask-advisor-btn-inner"
          onClick={handleAskAdvisor}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 4,
            padding: '2px 8px 2px 4px',
            borderRadius: 12, // matches wrapper, box-shadow creates uniform border
            border: 'none',
            backgroundColor: '#FFFFFF',
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: 'Roboto',
            fontWeight: 400,
            color: theme.colors.primary,
            whiteSpace: 'nowrap',
            height: 22, // 24 - 2px for gradient border (1px each side)
            flexShrink: 0,
            boxSizing: 'border-box',
            lineHeight: '20px',
            transition: 'background-color 0.15s ease'
          }}
        >
          <AiIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
          Ask Advisor
        </button>
      </span>
    </span>
  ) : undefined;

  return (
    <SearchTextField
      variant="standard"
      value={input}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          // TODO: fix types, verify functionality
          // @ts-ignore
          handleSearchChange(input, -1, appName, retailer, mainTimePeriod, history.push);
        }
      }}
      onChange={(event) => setInput(event.target.value)}
      endAdornment={askAdvisorButton}
      customWidth={showAdvisorButton ? 400 : 290}
    />
  );
};

export default withRouter(NewSearchInput);
